// THE REP'S EMAIL SIGNATURE — the lines that go under the sign-off of every
// outreach draft the app stages:
//
//   …What does your availability look like this week or early next?
//
//   Best,
//   Jixiang Huang
//   Revailo
//   jixianghuang66@gmail.com
//
// WHERE IT LIVES. On the account (GET/POST /api/gmail/signature, one row per
// user in the integrations table), so the same signature follows the rep to
// every device — the same reason the profile name lives in Cognito rather than
// in a browser. This browser keeps a per-account copy in localStorage so the
// cards can append it synchronously and offline; the account copy wins on the
// next successful read.
//
// WHEN IT IS APPLIED. At RENDER and COPY time, by the card that shows the
// draft (withSignature below) — never baked into the stored draft. Two
// reasons: a receipt is a record of what the run produced, and the model
// (enrich/handler.py#_draft) is told to end on "Best," with no name so that
// exactly one signature — this one — is ever added; and a teammate reading
// the same synced receipt should see the draft signed as THEMSELVES, not as
// whoever ran it. Change the signature in Settings and every card, past and
// future, updates.
//
// The DEFAULT is built from the profile: name, workspace, email. It is what
// the Settings editor is seeded with when nothing has been saved yet, and what
// the cards use until the rep saves something — a draft never goes out signed
// with an empty line.
import { useEffect, useState } from 'react'
import { displayName, getIdToken } from './cognito.js'
import { scopedKey } from './userScope.js'

const CACHE_BASE = 'revailosales.signature'
const cacheKey = () => scopedKey(CACHE_BASE)
const listeners = new Set()

// Same-origin first, then the deployed API (local dev has no /api proxy — the
// same fallback every client in this app uses).
const GMAIL_FALLBACK_BASE = 'https://hzpio22kcl.execute-api.us-east-1.amazonaws.com/prod'

export const SIGNATURE_MAX = 600
export const SIGNATURE_LINES_MAX = 8
export const WORKSPACE_NAME = 'Revailo'

/** Normalise the way the server does: no \r, no trailing spaces, no blank runs, bounded. */
export function cleanSignature(raw) {
  const lines = String(raw ?? '')
    .replace(/\r/g, '')
    .split('\n')
    .map((l) => l.replace(/\s+$/g, ''))
  const out = []
  for (const line of lines) {
    if (!line && (!out.length || !out[out.length - 1])) continue
    out.push(line)
  }
  while (out.length && !out[out.length - 1]) out.pop()
  return out.slice(0, SIGNATURE_LINES_MAX).join('\n').slice(0, SIGNATURE_MAX).trim()
}

/**
 * The signature the profile implies when nothing has been saved: the rep's
 * name (as Settings › Profile holds it), the workspace, and their address.
 * Every line is a fact the profile actually carries — a blank name is left
 * out rather than printed as an empty line.
 */
export function defaultSignature(user) {
  const email = String(user?.email || '').trim()
  // `user.name` is never blank — displayName() falls back to the email
  // local-part ("jixianghuang66" -> "Jixianghuang66") — so a name that IS that
  // fallback is no name at all, and the line is left out.
  const named = String(user?.name || '').trim()
  const name =
    named && named !== displayName({ email })
      ? named
      : [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
  return cleanSignature([name, WORKSPACE_NAME, email].filter(Boolean).join('\n'))
}

// ── the local copy ──────────────────────────────────────────────────────────
function readCache() {
  try {
    const raw = JSON.parse(localStorage.getItem(cacheKey()) || 'null')
    return raw && typeof raw === 'object' ? raw : null
  } catch {
    return null
  }
}

function writeCache(entry) {
  try {
    localStorage.setItem(cacheKey(), JSON.stringify(entry))
  } catch {
    /* quota / private mode — the in-memory value still applies this session */
  }
  for (const fn of listeners) {
    try {
      fn(entry)
    } catch {
      /* a bad subscriber must not break the others */
    }
  }
}

/**
 * The signature to append RIGHT NOW, synchronously: the saved one if this
 * account has one (from the account, or the local copy of it), else the
 * profile default. Never ''. `user` is the session's user object.
 */
export function currentSignature(user) {
  const cached = readCache()
  if (cached && typeof cached.signature === 'string' && cached.signature.trim()) return cached.signature
  return defaultSignature(user)
}

/** Whether the rep has SAVED a signature (vs. running on the profile default). */
export function hasSavedSignature() {
  const cached = readCache()
  return Boolean(cached && typeof cached.signature === 'string' && cached.signature.trim())
}

export function subscribeSignature(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

// ── the account copy ────────────────────────────────────────────────────────
async function authHeaders(extra = {}) {
  let token = ''
  try {
    token = (await getIdToken()) || ''
  } catch {
    token = ''
  }
  return { ...extra, ...(token ? { Authorization: `Bearer ${token}` } : {}) }
}

async function apiFetch(init) {
  let res = await fetch('/api/gmail/signature', init)
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) res = await fetch(`${GMAIL_FALLBACK_BASE}/gmail/signature`, init)
  const ct2 = res.headers.get('content-type') || ''
  if (!ct2.includes('application/json')) throw new Error('Signature API unavailable')
  const data = await res.json()
  if (!res.ok) {
    const err = new Error(data.error || `Signature API error (${res.status})`)
    err.status = res.status
    throw err
  }
  return data
}

/**
 * Read the account's signature and refresh the local copy. Resolves the
 * signature ('' when the account holds none). A failed read leaves the local
 * copy alone and resolves null — a card keeps appending what it has.
 */
// Fifty cards mount at once and every one asks; ONE request answers them all,
// and a fresh answer is reused for a minute rather than re-fetched per mount.
let signatureInflight = null
let signatureLoadedAt = 0
const SIGNATURE_FRESH_MS = 60_000

export async function loadSignature({ force = false } = {}) {
  if (signatureInflight) return signatureInflight
  if (!force && Date.now() - signatureLoadedAt < SIGNATURE_FRESH_MS) {
    return readCache()?.signature ?? ''
  }
  signatureInflight = (async () => {
    try {
      const headers = await authHeaders()
      // No ID token (demo session / signed out): the account copy is out of
      // reach and the API would only answer 401 — the browser copy is it.
      if (!headers.Authorization) return readCache()?.signature ?? ''
      const data = await apiFetch({ headers })
      const signature = cleanSignature(data.signature)
      if (signature) writeCache({ signature, updatedAt: data.updatedAt || '', source: 'account' })
      else if (readCache()?.source === 'account') writeCache({ signature: '', updatedAt: '', source: 'account' })
      signatureLoadedAt = Date.now()
      return signature
    } catch {
      return null
    } finally {
      signatureInflight = null
    }
  })()
  return signatureInflight
}

/**
 * Save to the account. Resolves { signature, saved: 'account' | 'browser' }:
 * 'browser' when the API could not be reached (demo session, offline) — the
 * signature still applies here, and the caller says so.
 */
export async function saveSignature(raw) {
  const signature = cleanSignature(raw)
  const headers = await authHeaders({ 'Content-Type': 'application/json' })
  // No ID token (demo session / signed out): there is no account to save to,
  // and the API would only answer 401 — the browser copy is the save.
  if (!headers.Authorization) {
    writeCache({ signature, updatedAt: new Date().toISOString(), source: 'browser' })
    return { signature, saved: 'browser' }
  }
  try {
    const data = await apiFetch({
      method: 'POST',
      headers,
      body: JSON.stringify({ signature }),
    })
    const saved = cleanSignature(data.signature)
    writeCache({ signature: saved, updatedAt: data.updatedAt || '', source: 'account' })
    return { signature: saved, saved: 'account' }
  } catch (err) {
    if (err?.status === 401 || err?.status === 403) throw err
    writeCache({ signature, updatedAt: new Date().toISOString(), source: 'browser' })
    return { signature, saved: 'browser' }
  }
}

// ── applying it ─────────────────────────────────────────────────────────────

// A closing line the drafts end on. Every draft the model or the template
// writes ends on "Best," alone (the prompt and outreachDraft.js both say so);
// the others are here so a hand-edited or older draft signs correctly too.
const SIGN_OFF_RE = /^(best|best regards|kind regards|regards|thanks|thank you|many thanks|cheers|sincerely|warm regards|all the best),?\s*$/i

/**
 * The email body WITH the signature under its sign-off:
 *
 *   "…early next?\n\nBest,"            → "…early next?\n\nBest,\nJixiang Huang\nRevailo\n…"
 *   "…early next?\n\nBest,\nJixiang…"  → unchanged (already signed with this signature)
 *   "…early next?"  (no sign-off)      → "…early next?\n\nBest,\nJixiang Huang\n…"
 *
 * Idempotent, and it never adds a second sign-off: a body whose last paragraph
 * is "Best,\n<something else>" (an older draft the template signed with a
 * sender name) is left exactly as it is.
 */
export function withSignature(bodyText, signature) {
  const body = String(bodyText ?? '').replace(/\s+$/g, '')
  // Settings says the signature goes under "Best,", and some reps type that
  // closing into it anyway ("Best,\nJixiang…"): the sign-off is the body's job,
  // so a leading one is lifted off the signature rather than printed twice.
  const sigLines = cleanSignature(signature).split('\n')
  const closing = SIGN_OFF_RE.test(sigLines[0]) ? sigLines.shift() : 'Best,'
  const sig = sigLines.join('\n')
  if (!sig) return body
  if (!body) return body
  if (body.endsWith(`\n${sig}`)) return body
  const paragraphs = body.split(/\n{2,}/)
  const last = paragraphs[paragraphs.length - 1] || ''
  const lastLines = last.split('\n')
  // The sign-off on the last line (alone in its paragraph, or under a closing
  // sentence in the same one): put the signature straight under it.
  if (SIGN_OFF_RE.test(lastLines[lastLines.length - 1])) return `${body}\n${sig}`
  // A sign-off already followed by other lines (a name the writer put there):
  // leave it — this draft is signed already, just not by us.
  if (lastLines.some((line) => SIGN_OFF_RE.test(line))) return body
  // No sign-off at all: add one, then the signature.
  return `${body}\n\n${closing}\n${sig}`
}

// ── React binding ───────────────────────────────────────────────────────────
//
// The signature as a hook: the current value (saved, else the profile default),
// re-rendered on any save in this tab, and refreshed from the account once per
// mount. `user` is the session's user (useAuth().user).
export function useSignature(user) {
  const [value, setValue] = useState(() => currentSignature(user))
  useEffect(() => {
    setValue(currentSignature(user))
    const off = subscribeSignature(() => setValue(currentSignature(user)))
    let alive = true
    loadSignature().then(() => {
      if (alive) setValue(currentSignature(user))
    })
    return () => {
      alive = false
      off()
    }
    // The default depends on the profile fields only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name, user?.email, user?.firstName, user?.lastName])
  return value
}
